import { useRef, useState } from "react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import {
  X,
  User,
  Users,
  Activity,
  Package,
  ClipboardList,
  Search,
  ChevronRight,
  Layers,
  Store,
} from "lucide-react";
import { PosVisitaModal } from "./PosVisitaModal";
import { ObjetivoProximaVisitaModal } from "./ObjetivoProximaVisitaModal";
import { RupturaModal } from "./RupturaModal";
import { EstoqueModal } from "./EstoqueModal";
import { PesquisaMercadoModal } from "./PesquisaMercadoModal";

export interface PesquisasModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const sections = [
  {
    title: "Geral",
    description: "Acompanhamento de visitas",
    icon: Layers,
    items: [
      { label: "Pós Visita", icon: User, hint: "Registre o resultado da visita" },
      { label: "Objetivo próxima Visita", icon: Users, hint: "Defina metas e próximos passos" },
    ],
  },
  {
    title: "No PDV",
    description: "Pesquisas no ponto de venda",
    icon: Store,
    items: [
      { label: "Ruptura", icon: Activity, hint: "Identifique faltas de produtos" },
      { label: "Estoque", icon: Package, hint: "Contagem de medicamentos" },
      { label: "Pesquisa Mercado", icon: ClipboardList, hint: "Concorrência e preços" },
    ],
  },
];

export function PesquisasModal({ open, onOpenChange }: PesquisasModalProps) {
  const [posVisitaOpen, setPosVisitaOpen] = useState(false);
  const [objetivoProximaVisitaOpen, setObjetivoProximaVisitaOpen] = useState(false);
  const [rupturaOpen, setRupturaOpen] = useState(false);
  const [estoqueOpen, setEstoqueOpen] = useState(false);
  const [pesquisaMercadoOpen, setPesquisaMercadoOpen] = useState(false);
  const guardCloseRef = useRef(false);

  const childOpen =
    posVisitaOpen || objetivoProximaVisitaOpen || rupturaOpen || estoqueOpen || pesquisaMercadoOpen;

  const guardOnClose = (next: boolean, setter: (v: boolean) => void) => {
    if (!next) {
      guardCloseRef.current = true;
      window.setTimeout(() => {
        guardCloseRef.current = false;
      }, 350);
    }
    setter(next);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          onInteractOutside={(e) => {
            if (childOpen || guardCloseRef.current) e.preventDefault();
          }}
          onFocusOutside={(e) => {
            if (childOpen || guardCloseRef.current) e.preventDefault();
          }}
          className="w-full max-w-md overflow-hidden rounded-2xl border-none p-0 shadow-2xl [&>button]:hidden"
        >
          <div className="relative overflow-hidden bg-gradient-to-br from-tudo-blue via-tudo-blue to-tudo-blue/80 px-5 py-5">
            <div className="pointer-events-none absolute -right-8 -top-10 h-32 w-32 rounded-full bg-white/10 blur-xl" />
            <div className="pointer-events-none absolute -bottom-12 -left-6 h-28 w-28 rounded-full bg-tudo-green/20 blur-xl" />
            <div className="relative flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-white/15 shadow-inner ring-1 ring-white/20 backdrop-blur-sm">
                  <Search className="h-5 w-5 text-white" />
                </div>
                <div>
                  <DialogTitle className="text-left text-lg font-bold tracking-wide text-white">
                    Pesquisas
                  </DialogTitle>
                  <p className="text-xs font-medium text-white/70">
                    Selecione um tipo de pesquisa
                  </p>
                </div>
              </div>
              <button
                onClick={() => onOpenChange(false)}
                className="rounded-lg p-1.5 text-white/80 transition-colors hover:bg-white/10 hover:text-white"
                aria-label="Fechar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="max-h-[70vh] space-y-5 overflow-y-auto bg-background p-4">
            {sections.map((section) => {
              const SectionIcon = section.icon;
              return (
                <div key={section.title}>
                  <div className="mb-2.5 flex items-center gap-2 px-1">
                    <div className="flex h-7 w-7 items-center justify-center rounded-lg bg-tudo-blue/10">
                      <SectionIcon className="h-4 w-4 text-tudo-blue" />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold uppercase tracking-wide text-tudo-blue">
                        {section.title}
                      </h3>
                      <p className="text-[11px] leading-tight text-muted-foreground">
                        {section.description}
                      </p>
                    </div>
                    <div className="ml-2 h-px flex-1 bg-gradient-to-r from-border to-transparent" />
                  </div>
                  <div className="space-y-2">
                    {section.items.map((item, idx) => {
                      const Icon = item.icon;
                      return (
                        <button
                          key={item.label}
                          type="button"
                          style={{ animationDelay: `${idx * 60}ms` }}
                          onClick={() => {
                            if (item.label === "Pós Visita") {
                              setPosVisitaOpen(true);
                            } else if (item.label === "Objetivo próxima Visita") {
                              setObjetivoProximaVisitaOpen(true);
                            } else if (item.label === "Ruptura") {
                              setRupturaOpen(true);
                            } else if (item.label === "Estoque") {
                              setEstoqueOpen(true);
                            } else if (item.label === "Pesquisa Mercado") {
                              setPesquisaMercadoOpen(true);
                            }
                          }}
                          className="group flex w-full animate-in fade-in slide-in-from-bottom-2 items-center gap-4 rounded-xl border border-border bg-card px-3 py-3 text-left shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:border-tudo-blue hover:shadow-lg hover:shadow-tudo-blue/10 focus:outline-none focus:ring-2 focus:ring-tudo-blue focus:ring-offset-2"
                        >
                          <div className="relative flex h-12 w-12 shrink-0 items-center justify-center overflow-hidden rounded-xl bg-gradient-to-br from-tudo-blue/10 to-tudo-blue/5 transition-all duration-200 group-hover:from-tudo-blue group-hover:to-tudo-blue/80">
                            <div className="absolute inset-y-0 left-0 w-1 bg-tudo-blue transition-all group-hover:w-1.5 group-hover:bg-tudo-green" />
                            <Icon className="h-6 w-6 text-tudo-blue transition-colors duration-200 group-hover:text-white" />
                          </div>
                          <div className="flex-1">
                            <span className="block text-base font-semibold text-foreground transition-colors group-hover:text-tudo-blue">
                              {item.label}
                            </span>
                            <span className="block text-xs text-muted-foreground">
                              {item.hint}
                            </span>
                          </div>
                          <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground/40 transition-all duration-200 group-hover:translate-x-1 group-hover:text-tudo-blue" />
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>
        </DialogContent>
      </Dialog>

      <PosVisitaModal open={posVisitaOpen} onOpenChange={(v) => guardOnClose(v, setPosVisitaOpen)} />
      <ObjetivoProximaVisitaModal
        open={objetivoProximaVisitaOpen}
        onOpenChange={(v) => guardOnClose(v, setObjetivoProximaVisitaOpen)}
      />
      <RupturaModal open={rupturaOpen} onOpenChange={(v) => guardOnClose(v, setRupturaOpen)} />
      <EstoqueModal open={estoqueOpen} onOpenChange={(v) => guardOnClose(v, setEstoqueOpen)} />
      <PesquisaMercadoModal open={pesquisaMercadoOpen} onOpenChange={(v) => guardOnClose(v, setPesquisaMercadoOpen)} />
    </>
  );
}
